// src/pages/income/business/IncomeBusinessNew.jsx
import React, { useEffect, useMemo, useState } from "react";
import styles from "./IncomeBusinessNew.module.css";
import api from "../../../api";
import { useTabActivated } from "../../../tabs/TabActivation";
import InvoiceForm from "./InvoiceForm";  // 청구서
import StatementForm from "./StatementForm"; // 거래명세서

import ApprovalLine from "../../../components/approval-line/ApprovalLine"; //결재 라인
import ApprovalLineModal from "../../../components/approval-line/ApprovalLineModal";
import IncomeProposalPreview from "../../../components/preview/income/IncomeProposalPreview"; // 미리보기



const PAY_METHOD_LABELS = ["계좌이체", "카드결제", "현금결제"];
const PAY_METHOD_TO_CODE = { "계좌이체": "transfer", "카드결제": "card", "현금결제": "cash" };
const CODE_TO_PAY_METHOD = { transfer: "계좌이체", card: "카드결제", cash: "현금결제" };

export default function IncomeBusinessNew({ fixedCategory = "경영팀" }) {

  const [activeTab, setActiveTab] = useState("draft");
  const [rightTab, setRightTab] = useState("approval"); // ✅ 오른쪽 패널 탭 상태
  const [category] = useState(fixedCategory);

  const [subcategory, setSubcategory] = useState("예금 이자수익");  // 세부구분 기본값
  const [draftDate, setDraftDate] = useState(() => toISO(new Date()));
  const [claimDate, setClaimDate] = useState(() => toISO(new Date()));  // ✅ 초기값 설정
  const [workMonth, setWorkMonth] = useState("");
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [title, setTitle] = useState("");
  const [seq, setSeq] = useState("001");
  const [loadingSeq, setLoadingSeq] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [approvers, setApprovers] = useState([]);

  // ✅ 거래처 관련 상태
  const [vendors, setVendors] = useState([]);
  const [vendorInput, setVendorInput] = useState("");
  const [vendorId, setVendorId] = useState(null);
  const [showVendorList, setShowVendorList] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const [payMethodLabel, setPayMethodLabel] = useState("계좌이체");

  const yymmdd = useMemo(() => draftDate.replaceAll("-", "").slice(2), [draftDate]);
  const draftNo = useMemo(
    () => `금천일자리(주)경영-${yymmdd}-${seq}호`,
    [category, yymmdd, seq]
  );

  // ✅ 작성일 변경 시 청구일 자동 설정
  useEffect(() => {
    setClaimDate(draftDate);
  }, [draftDate]);




  // ✅ seq 자동 생성 (경영팀용)
  // ✅ keep-alive 탭 재방문 시 다음 번호를 다시 조회(마운트가 유지돼 처음 조회값에 멈춰 있지 않게)
  const [seqRefreshTick, setSeqRefreshTick] = useState(0);
  useTabActivated(() => setSeqRefreshTick((t) => t + 1));
  useEffect(() => {
    let ignore = false;
    async function getSeq() {
      if (!category || !draftDate) return;
      setLoadingSeq(true);
      try {
        const { data } = await api.get(`/income/business/next-seq/?date=${draftDate}`);
        if (!ignore) setSeq(String(data.next_seq).padStart(3, "0"));
      } catch (e) {
        console.error("[next-seq] failed:", e?.response?.status);
      } finally {
        if (!ignore) setLoadingSeq(false);
      }
    }
    getSeq();
    return () => { ignore = true; };
  }, [category, draftDate, seqRefreshTick]);

  // ✅ 거래처 목록 불러오기
  useEffect(() => {
    async function fetchVendors() {
      setLoadingVendors(true);
      try {
        const res = await api.get("vendors/");
        const data = Array.isArray(res.data) ? res.data : res.data.results || [];
        setVendors(data);
      } catch (err) {
        console.error("❌ 거래처 목록 불러오기 실패:", err);
      } finally {
        setLoadingVendors(false);
      }
    }
    fetchVendors();
  }, []);

  // ✅ 거래처 자동완성 필터
  const filteredVendors = vendors.filter((v) =>
    v.name.toLowerCase().includes(vendorInput.toLowerCase())
  );

  // ✅ 거래처 입력 시
  const handleChangeVendor = (e) => {
    setVendorInput(e.target.value);
    setVendorId(null);
    setShowVendorList(true);
    setHighlightIndex(-1);
  };

  // ✅ 거래처 선택
  const handlePickVendor = (v) => {
    setVendorId(v.id);
    setVendorInput(v.name);
    setShowVendorList(false);
    setHighlightIndex(-1);
  };

  // ✅ 방향키 이동 및 Enter 선택
  const handleVendorKeyDown = (e) => {
    if (!showVendorList || filteredVendors.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev < filteredVendors.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev > 0 ? prev - 1 : filteredVendors.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0 && filteredVendors[highlightIndex]) {
        handlePickVendor(filteredVendors[highlightIndex]);
      }
    }
  };

  // ✅ 제목 자동 생성
  useEffect(() => {
    if (vendorInput && subcategory && workMonth) {
      let year = "", month = "";

      if (workMonth.includes("-")) {
        [year, month] = workMonth.split("-");
      } else if (workMonth.includes("년")) {
        year = workMonth.split("년")[0].trim();
        month = workMonth.split("년")[1].replace("월", "").trim();
      }

      if (month && month.length === 1) month = "0" + month;

      const cleanVendor = vendorInput.replace(/\(.*?\)/g, "").trim();

      if (year && month) {
        setTitle(`${year}년 ${month}월 ${cleanVendor} ${subcategory}`);
      }
    } else {
      setTitle("");
    }
  }, [vendorInput, subcategory, workMonth]);

  // ✅ 결재자 선택 핸들러
  const handleSelectApprovers = (selectedUsers) => {
    console.log("✅ 선택된 결재자:", selectedUsers);
    const newApprovers = selectedUsers.map((user, idx) => ({
      id: user.id || `new-${Date.now()}-${idx}`,
      name: user.name,
      position: user.position,  // ✅ position 유지!
      status: user.status,
    }));
    setApprovers((prev) => [...prev, ...newApprovers]);
  };

  // ✅ 저장 기능 (경영팀용)
  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);

    if (!subcategory || !title || !amount || !vendorInput) {
      alert("필수 항목을 모두 입력해 주세요.");
      setSaving(false);
      return;
    }

    let safeWorkMonth = workMonth;
    if (safeWorkMonth.includes("년")) {
      safeWorkMonth = safeWorkMonth
        .replace("년 ", "-")
        .replace("월", "")
        .trim();
    }

    const payload = {
      category,
      subcategory,
      title,
      draft_date: draftDate,
      claim_date: claimDate || null,           // ✅ 수정
      work_month: safeWorkMonth,
      partner: vendorId || null,               // ✅ partner_id → partner
      partner_name: vendorInput,
      amount: amount ? Number(amount) : 0,     // ✅ 수정
      pay_method: PAY_METHOD_TO_CODE[payMethodLabel] || "transfer",
      desc: desc || "",                        // ✅ 수정
      seq,
      draft_no: draftNo,
    };

    try {
      const { data } = await api.post(`/income/business/`, payload);
      setSavedId(data.id);
      alert(`✅ 저장 완료!\n거래처: ${vendorInput}\n금액: ${amount}원`);
    } catch (err) {
      console.error("❌ 저장 실패:", err.response?.data || err);
      alert("서버 저장 실패. 관리자에게 문의하세요.");
    } finally {
      setSaving(false);
    }
  };

  // ✅ 내보내기 기능
  const download = (fmt) => {
    if (!savedId) return alert("먼저 저장해 주세요.");
    window.open(`${api.defaults.baseURL}income-drafts/${savedId}/export?fmt=${fmt}`, "_blank");
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.split}>

        <div className={styles.pane}>
          {activeTab === "draft" && (
            <form className={styles.form} onSubmit={onSubmit}>
              <h3 style={{ marginBottom: "10px" }}>경영팀 수입기안서 작성</h3>

              <Row label="기안번호(자동완성)">
                <input value={draftNo} readOnly />
              </Row>

              <Row label="제목(자동완성)">
                <input
                  value={title}
                  readOnly
                  placeholder="청구일·작업월·거래처 입력 시 자동 생성됩니다."
                />
              </Row>

              <Row label="사업구분"><input value={category} readOnly /></Row>

              <Row label="세부구분">
                <select
                  value={subcategory}
                  onChange={(e) => setSubcategory(e.target.value)}
                  className={styles.selectBox}
                >
                  <option value="예금 이자수익">예금 이자수익</option>
                  <option value="장애인고용장려금">장애인고용장려금</option>
                  <option value="고령자친화기업 인센티브">고령자친화기업 인센티브</option>
                  <option value="청년 정규직 채용 장려금">청년 정규직 채용 장려금</option>
                  <option value="카드포인트">카드포인트</option>
                  <option value="카드매출 수수료 환급">카드매출 수수료 환급</option>
                  <option value="기타영업외수익">기타영업외수익</option>
                  <option value="출자금">출자금</option>
                  <option value="전년도 이월액">전년도 이월액</option>
                </select>
              </Row>

              <Row label="작성일">
                <input type="date" value={draftDate} onChange={(e) => setDraftDate(e.target.value)} />
              </Row>

              <Row label="청구일">
                <input type="date" value={claimDate} onChange={(e) => setClaimDate(e.target.value)} />
              </Row>

              <Row label="작업월(귀속월)">
                <input type="month" value={workMonth} onChange={(e) => setWorkMonth(e.target.value)} />
              </Row>

              <Row label="거래처">
                <div className={styles.partnerRow}>
                  <input
                    placeholder="직접 입력 또는 검색"
                    value={vendorInput}
                    onChange={handleChangeVendor}
                    onKeyDown={handleVendorKeyDown}
                  />
                  <button
                    type="button"
                    className={styles.partnerButton}
                    onClick={() => setShowVendorList((v) => !v)}
                  >
                    ▼
                  </button>
                </div>
                {showVendorList && (
                  <div className={styles.partnerBox}>
                    {loadingVendors ? (
                      <div>불러오는 중...</div>
                    ) : filteredVendors.length > 0 ? (
                      filteredVendors.map((v, i) => (
                        <div
                          key={v.id}
                          className={`${styles.partnerItem} ${i === highlightIndex ? styles.activeItem : ""}`}
                          onClick={() => handlePickVendor(v)}
                        >
                          {v.name}
                        </div>
                      ))
                    ) : (
                      <div>일치하는 거래처가 없습니다.</div>
                    )}
                  </div>
                )}
              </Row>

              <Row label="금액">
                <input
                  inputMode="numeric"
                  placeholder="숫자만"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ""))}
                />
              </Row>

              <Row label="거래방법">
                <div className={styles.inline}>
                  {PAY_METHOD_LABELS.map((label) => (
                    <label key={label} className={styles.check}>
                      <input
                        type="checkbox"
                        checked={payMethodLabel === label}
                        onChange={() => setPayMethodLabel(label)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </Row>

              <Row label="본문/비고">
                <textarea
                  rows={4}
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                  placeholder="청구 관련 메모를 입력하세요."
                />
              </Row>

              <div className={styles.actions}>
                <button type="submit" disabled={saving} className={styles.primaryBtn}>
                  {saving ? "저장 중..." : "저장"}
                </button>
              </div>

              <div className={styles.actions}>
                <button type="button" onClick={() => download("docx")} className={styles.primaryBtn}>한글(DOCX)</button>
                <button type="button" onClick={() => download("pdf")} className={styles.primaryBtn}>PDF</button>
              </div>
            </form>
          )}
          {activeTab === "invoice" && <InvoiceForm />}
          {activeTab === "statement" && <StatementForm />}
        </div>

        {/* 가운데: 결재라인 */}
        <div className={styles.middlePane}>
          <ApprovalLine 
            approvers={approvers}
            setApprovers={setApprovers}
            onOpenModal={() => setShowModal(true)}
          />
        </div>

        
        {/* 오른쪽: 미리보기 */}
        <div className={styles.rightPane}>
          <IncomeProposalPreview 
            formData={{
              recipient: "내부결재",
              title: title,
              businessType: category,
              detailType: subcategory,
              workMonth: workMonth,        // ✅ 추가!
              startDate: workMonth,
              endDate: workMonth,
              client: vendorInput,
              amount: amount,
              transactionType: {
                계좌이체: payMethodLabel === "계좌이체",
                카드결제: payMethodLabel === "카드결제",
                현금결제: payMethodLabel === "현금결제"
              },
              manualDate: claimDate,
              documentNumber: draftNo,
              content: desc
            }}
            approvalLine={approvers}
          />
        </div>
      </div>
      
      {/* ✅ 모달 추가 */}
      {showModal && (
        <ApprovalLineModal
          onClose={() => setShowModal(false)}
          onSelect={handleSelectApprovers}
        />
      )}
    </div>
  );
}

// ✅ 공통 Row
function Row({ label, children }) {
  return (
    <div className={styles.row}>
      <div className={styles.rowLabel}>{label}</div>
      <div className={styles.rowField}>{children}</div>
    </div>
  );
}

// ✅ 날짜 포맷 함수
function toISO(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
